import React, { useEffect, useRef, useState } from "react";
import { Button } from "newui/components/Button";
import { CompactTextInput } from "newui/components/ui/InputWithLabel";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { preComplianceSteps } from "guides/features/Evaluation";
import type { ComplianceQuestion } from "newui/features/Rfx/Evaluation/types/compliance";
import {
	useComplianceQuestions,
	useSaveComplianceQuestions,
} from "newui/features/Rfx/Evaluation/hooks/useCompliance";
import {
	QuestionsLoading,
	QuestionsError,
	QuestionsEmpty,
	QuestionsList,
} from "./components";

const PRE_COMPLIANCE_INSTRUCTIONS: React.ReactNode[] = [
	"Confirm the compliance questions suppliers will be checked against for this RFx.",
	<>
		You can edit, add, remove and reorder questions before proceeding. Click{" "}
		<strong>Show Me</strong> for a quick walkthrough of adding a question.
	</>,
];

export interface Stage1ConfirmQuestionsProps {
	rfxId: number;
	onProceed: () => void;
	onSkip: () => void;
}

export const Stage1ConfirmQuestions = ({
	rfxId,
	onProceed,
	onSkip,
}: Stage1ConfirmQuestionsProps) => {
	const { data, isLoading, isError, refetch } = useComplianceQuestions(rfxId);
	const saveMutation = useSaveComplianceQuestions(rfxId);

	// Working copy of the question list edited in memory; persisted as one PUT.
	const [questions, setQuestions] = useState<ComplianceQuestion[]>([]);
	const [newQuestion, setNewQuestion] = useState("");
	const [saveError, setSaveError] = useState<string | null>(null);

	/**
	 * Local-only id assigned to questions added in this session before they have
	 * a server id. Negative so it never collides with a persisted id; stripped
	 * before the PUT payload is sent (`id` is omitted for new rows). Held in a ref
	 * so the counter is per-component-instance (no cross-instance/test leakage).
	 */
	const nextLocalId = useRef(-1);
	const makeLocalId = (): number => {
		const id = nextLocalId.current;
		nextLocalId.current -= 1;
		return id;
	};

	// Seed the working copy once, when the server data first arrives. Guarded by a
	// ref so a background refetch doesn't overwrite in-progress unsaved edits.
	const seeded = useRef(false);
	useEffect(() => {
		if (!seeded.current && data?.questions) {
			setQuestions(data.questions);
			seeded.current = true;
		}
	}, [data]);

	const isFromGlobalDefaults = data?.source === "global";

	const handleEdit = (id: number, question: string) => {
		setQuestions((prev) =>
			prev.map((q) => (q.id === id ? { ...q, question } : q))
		);
	};

	const handleDelete = (id: number) => {
		setQuestions((prev) => prev.filter((q) => q.id !== id));
	};

	const handleAdd = () => {
		const trimmed = newQuestion.trim();
		if (trimmed.length === 0) {
			return;
		}
		setQuestions((prev) => [
			...prev,
			{
				id: makeLocalId(),
				question: trimmed,
				sort_order: prev.length + 1,
			},
		]);
		setNewQuestion("");
	};

	const handleNewKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleAdd();
		}
	};

	// At least one compliance question is required before proceeding to the grid —
	// an empty set would render a grid with nothing to assess.
	const hasNoQuestions = questions.length === 0;

	const handleProceed = () => {
		setSaveError(null);
		if (hasNoQuestions) {
			setSaveError(
				"Add at least one compliance question before proceeding, or use Skip Compliance Check."
			);
			return;
		}
		const payload = {
			questions: questions.map((q, index) => ({
				// Omit id for locally-added rows (negative sentinel ids).
				...(q.id > 0 ? { id: q.id } : {}),
				question: q.question,
				sort_order: index + 1,
			})),
		};
		saveMutation.mutate(payload, {
			onSuccess: () => onProceed(),
			onError: () =>
				setSaveError(
					"We couldn't save the compliance questions. Please try again."
				),
		});
	};

	const renderList = () => {
		if (isLoading) {
			return <QuestionsLoading />;
		}

		if (isError) {
			return <QuestionsError onRetry={refetch} />;
		}

		if (questions.length === 0) {
			return <QuestionsEmpty />;
		}

		return (
			<QuestionsList
				questions={questions}
				onEdit={handleEdit}
				onDelete={handleDelete}
				onReorder={setQuestions}
			/>
		);
	};

	return (
		<div data-testid="pre-compliance-stage-1">
			<InstructionsInfoBox
				data-testid="pre-compliance-info"
				instructions={PRE_COMPLIANCE_INSTRUCTIONS}
				guideSteps={preComplianceSteps}
			/>

			{isFromGlobalDefaults && (
				<div
					className="mt-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"
					data-testid="pre-compliance-global-banner"
				>
					Loaded from company defaults — you may edit before
					proceeding.
				</div>
			)}

			<div className="mt-6">
				<div className="border-b border-gray-200 px-3 py-2">
					<span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
						Compliance Question
					</span>
				</div>

				<div className="mt-2">{renderList()}</div>

				<div className="mt-4 flex items-center gap-3">
					<div className="min-w-0 flex-1">
						<CompactTextInput
							value={newQuestion}
							placeholder="Add a new compliance question..."
							data-testid="pre-compliance-add-input"
							onChange={(e) => setNewQuestion(e.target.value)}
							onKeyDown={handleNewKeyDown}
						/>
					</div>
					<Button
						btntype="secondary"
						disabled={newQuestion.trim().length === 0}
						onClick={handleAdd}
						data-testid="pre-compliance-add-btn"
					>
						Add question
					</Button>
				</div>
			</div>

			{saveError && (
				<p
					className="mt-4 text-sm text-red-600"
					data-testid="pre-compliance-save-error"
				>
					{saveError}
				</p>
			)}

			<div className="mt-6 flex items-center justify-between">
				<Button
					btntype="danger"
					onClick={onSkip}
					data-testid="pre-compliance-skip-btn"
				>
					Skip Compliance Check
				</Button>

				<Button
					btntype="primary"
					isLoading={saveMutation.isPending}
					disabled={
						saveMutation.isPending ||
						isLoading ||
						isError ||
						hasNoQuestions
					}
					onClick={handleProceed}
					data-testid="pre-compliance-proceed-btn"
				>
					Proceed to Compliance Check
				</Button>
			</div>
		</div>
	);
};
